'use client';

/**
 * Active industry-solution key store — a lightweight mirror of
 * OperationsContext.activeSolutionKey.
 *
 * Lightweight hooks (e.g. useWorkflowActionPermissions) need the active
 * solution to overlay per-solution permission slices, but importing
 * OperationsContext pulls in masterSync → Dexie, which breaks pure-module
 * test environments. This module holds only the key, kept in sync by
 * OperationsContext, so dependents stay free of heavy transitive imports.
 */

import { useSyncExternalStore } from 'react';

let activeSolutionKey: string | null = null;
const listeners = new Set<() => void>();

export function setActiveSolutionKeyMirror(solutionKey: string | null): void {
  if (activeSolutionKey === solutionKey) return;
  activeSolutionKey = solutionKey;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string | null {
  return activeSolutionKey;
}

function getServerSnapshot(): string | null {
  return null;
}

/** Reactive read of the active industry solution key (null when unset). */
export function useActiveSolutionKey(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
